/**
 * Particle / overdraw budget (pure logic). Effects queue emitter requests during a frame; at flush
 * time `arbitrate` grants particles highest-priority first, so when a screen region (or the whole
 * pool) is saturated the lowest-priority emitters are the ones that get thinned or dropped.
 *
 * Priorities (higher survives):
 *   5  danger: enemy telegraph slams, interrupts
 *   4  the human player's own impacts
 *   3  other impacts, enemy casts, deaths
 *   2  heals, aura bursts
 *   1  loops (burning, aura sparkle, trails)
 *   0  decoration (dust, ambient motes)
 */

export const PRIO = { decor: 0, loop: 1, support: 2, impact: 3, player: 4, danger: 5 } as const;

export interface BudgetRequest {
  priority: number;
  /** Particles wanted. */
  count: number;
  /** Screen region index (see `regionOf`). */
  region: number;
  /** Overdraw weight per particle. */
  cost: number;
}

export interface BudgetLimits {
  /** Max live overdraw cost per screen region. */
  regionCap: number;
  /** Free particle slots (whole pool). */
  globalFree: number;
}

/**
 * Grant counts for `reqs[0..n)` into `out`, highest priority first (ties: request order).
 * `regionLoad` is the live cost per region; it is not modified. `order` is scratch (length ≥ n).
 */
export function arbitrate(reqs: readonly BudgetRequest[], n: number, regionLoad: ArrayLike<number>, limits: BudgetLimits, out: Int32Array, order: Int32Array, scratchLoad: Float32Array): void {
  for (let i = 0; i < n; i++) order[i] = i;
  // Insertion sort by priority desc (stable; n is small, no allocation).
  for (let i = 1; i < n; i++) {
    const v = order[i]!;
    const p = reqs[v]!.priority;
    let j = i - 1;
    while (j >= 0 && reqs[order[j]!]!.priority < p) {
      order[j + 1] = order[j]!;
      j--;
    }
    order[j + 1] = v;
  }
  for (let r = 0; r < scratchLoad.length; r++) scratchLoad[r] = regionLoad[r] ?? 0;
  let free = limits.globalFree;
  for (let k = 0; k < n; k++) {
    const idx = order[k]!;
    const q = reqs[idx]!;
    const r = Math.max(0, Math.min(scratchLoad.length - 1, q.region | 0));
    const cost = Math.max(1e-4, q.cost);
    const roomRegion = Math.floor(Math.max(0, limits.regionCap - scratchLoad[r]!) / cost);
    const g = Math.max(0, Math.min(q.count, roomRegion, free));
    out[idx] = g;
    free -= g;
    scratchLoad[r] = scratchLoad[r]! + g * cost;
  }
}

/** Screen region for an NDC point (cols × rows grid; off-screen points clamp to the edge cells). */
export function regionOf(ndcX: number, ndcY: number, cols: number, rows: number): number {
  const cx = Math.max(0, Math.min(cols - 1, Math.floor(((ndcX + 1) / 2) * cols)));
  const cy = Math.max(0, Math.min(rows - 1, Math.floor(((ndcY + 1) / 2) * rows)));
  return cy * cols + cx;
}
